import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MatchGamesList from '../../../components/MatchGamesList';
import StreamLinks from '../../../components/StreamLinks';
import TeamRoster from '../../../components/TeamRoster';
import { apiGet, apiPost } from '../../../services/apiFetch';
import { formatDate, format12HourTime } from '../../../utils/formatters';
import placeholderImage from '../../../assets/images/placeholder.png';
import '../../../styles/AdminMatchDetails.css';

const MatchDetails = () => {
    const { id, date } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [match, setMatch] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('games');
    const [activeGame, setActiveGame] = useState(1);

    useEffect(() => {
        const loadMatchDetails = async () => {
            setIsLoading(true);
            try {
                if (location.state?.matchData) {
                    console.log("AdminMatchDetails.js: Using passed match data from MatchTable.js ", location.state.matchData);
                    setMatch(location.state.matchData);
                    setIsLoading(false);
                    return; // Exit early, no need to fetch
                }
                const { data, error } = await apiGet(`/api/rl/matches/upcoming/${id}?date=${date}`);
                if (error) throw new Error(error.error);
                console.log("AdminMatchDetails.js: Location state data failed to load, fetched match from dynamo db", data.match);
                setMatch(data.match);
            } catch (error) {
                console.error("AdminMatchDetails.js: error loading match details:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            loadMatchDetails();
        }
    }, [id, date]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleEdit = () => {
        console.log("AdminMatchDetails.js: handleEdit, passed match data to MatchForm.js via location state", "match: ", match, "status: ", status.toLowerCase());
        navigate(`/admin/matches/edit/${id}/${date}`, { state: { matchData: match, matchStatus: status.toLowerCase() } });
    };

    const handleSettle = () => {
        console.log("AdminMatchDetails.js: handleSettle, passed match data to SettleMatch.js via location state", "match: ", match);
        navigate(`/admin/matches/settle/${id}/${date}`, { state: { matchData: match } });
    };

    const handleMarkLive = async () => {
        // Show confirmation dialog
        const confirmed = window.confirm(
            `Are you sure you want to mark this match as LIVE?\n\n` +
            `${match.team_1_name} vs ${match.team_2_name}\n` +
            `Date: ${match.date} at ${match.time}\n\n` +
            `This action will move the match from upcoming to live status.`
        );

        // Only proceed if the user confirms
        if (!confirmed) {
            return;
        }

        const { data, error } = await apiPost(`/api/rl/matches/${id}/live`, match);
        console.log("AdminMatchDetails.js: marked match as live", data);
        if (!error) {
            navigate('/admin/matches');
        } else {
            console.error("AdminMatchDetails.js: error marking match as live:", error);
        }
    };

    const handleCancel = async () => {
        // Show confirmation dialog
        const confirmed = window.confirm(
            `Are you sure you want to CANCEL this match?\n\n` +
            `${match.team_1_name} vs ${match.team_2_name}\n` +
            `Date: ${match.date} at ${match.time}\n\n` +
            `⚠️ WARNING: This action will permanently cancel the match and cannot be undone.`
        );

        // Only proceed if the user confirms
        if (!confirmed) {
            return;
        }

        const { data, error } = await apiPost(`/api/rl/matches/${id}/cancel`, { gameid: id, date: date });
        console.log("AdminMatchDetails.js: cancelled match", data);
        if (!error) {
            navigate(-1);
        } else {
            console.error("AdminMatchDetails.js: error cancelling match:", error);
        }
    };

    if (isLoading) {
        return <div className="spinner"></div>;
    }

    if (!match) {
        return <div className="no-matches">Match not found</div>;
    }

    // Find team objects
    const team1 = match.teams.find(team => team.designation === 1);
    const team2 = match.teams.find(team => team.designation === 2);

    let team1Results;
    let team2Results;

    const statusMap = {
        'upcoming': { label: 'Upcoming', className: 'status-upcoming' },
        'live': { label: 'Live', className: 'status-live' },
        'completed': { label: 'Completed', className: 'status-completed' }
    };

    const statusInfo = match.match_status ? statusMap[match.match_status] :
        { label: 'Unknown', className: 'status-unknown' };

    let status = statusInfo.label;
    let statusClass = statusInfo.className;

    if (match.match_status === 'completed') {
        team1Results = team1?.results?.reduce((sum, item) => sum + item.result, 0) || 0;
        team2Results = team2?.results?.reduce((sum, item) => sum + item.result, 0) || 0;
    }

    // Format date and time
    const formattedDate = formatDate(match.date);
    const formattedTime = format12HourTime(match.time);

    // Get games count for selector
    const gamesCount = match.match_format.count;

    // Get player stats for active game
    const renderPlayerStats = (team) => {
        if (!team || !team.players) return null;

        return (
            <div className="player-stats-table">
                <div className="player-stats-header">
                    <div className="player-name">Player</div>
                    <div className="player-stat">Score</div>
                    <div className="player-stat">Goals</div>
                    <div className="player-stat">Assists</div>
                    <div className="player-stat">Saves</div>
                    <div className="player-stat">Shots</div>
                </div>
                {team.players.map(player => {
                    const gameStats = player.results?.find(result => result.game_number === activeGame);

                    // Check if all stats are 0 or undefined
                    const allStatsZero = !gameStats || (
                        (!gameStats.score || gameStats.score === 0) &&
                        (!gameStats.goals || gameStats.goals === 0) &&
                        (!gameStats.assists || gameStats.assists === 0) &&
                        (!gameStats.saves || gameStats.saves === 0) &&
                        (!gameStats.shots || gameStats.shots === 0)
                    );

                    return (
                        <div key={player.id} className={`player-stats-row ${allStatsZero ? 'did-not-play' : ''}`}>
                            <div className="player-name">
                                <div className="player-avatar-wrapper">
                                    <img
                                        src={player.image || placeholderImage}
                                        alt={player.username}
                                        className="player-avatar"
                                        onError={(e) => { e.target.src = placeholderImage }}
                                    />
                                </div>
                                <span className="player-username">{player.username}</span>
                            </div>
                            {allStatsZero ? (
                                <div className="did-not-play-message">Did not play in this match</div>
                            ) : (
                                <>
                                    <div className="player-stat">{gameStats?.score || '0'}</div>
                                    <div className="player-stat">{gameStats?.goals || '0'}</div>
                                    <div className="player-stat">{gameStats?.assists || '0'}</div>
                                    <div className="player-stat">{gameStats?.saves || '0'}</div>
                                    <div className="player-stat">{gameStats?.shots || '0'}</div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="match-details">
            <div className="admin-match-header">
                <div className='match-form-buttons'>
                    <button onClick={handleBack} className="back-button">
                        ← Back
                    </button>
                    <div className='match-form-buttons-right'>
                        {status !== "Live" && (
                            <button onClick={handleEdit} className="basic-button">
                                Edit
                            </button>
                        )}
                        {status !== 'Completed' && (
                            <>
                                <button onClick={handleCancel} className="basic-button">Cancel</button>
                                <button onClick={handleSettle} className="basic-button">Settle</button>
                            </>
                        )}
                        {status === 'Upcoming' && (
                            <button onClick={handleMarkLive} className="basic-button">
                                Mark as Live
                            </button>
                        )}
                    </div>
                </div>
                <div className={`admin-match-status ${statusClass}`}>{status}</div>
                <div className="match-title">
                    <h1>{match.team_1_name} vs {match.team_2_name}</h1>
                </div>
                <div className="match-vs">
                    <div className="match-team">
                        <img
                            src={team1?.image || placeholderImage}
                            alt={match.team_1_name}
                            className="team-logo-large"
                            onError={(e) => { e.target.src = placeholderImage }}
                        />
                        <div className="team-name-large">{match.team_1_name}</div>
                    </div>
                    {match.match_status === 'completed' ? (
                        <div className="match-score-large">
                            <span className={team1Results > team2Results ? 'game-winner' : 'game-loser'}>
                                {team1Results}
                            </span>
                            <span className="score-divider">:</span>
                            <span className={team1Results > team2Results ? 'game-winner' : 'game-loser'}>
                                {team2Results}
                            </span>
                        </div>
                    ) : (
                        <div className="vs-large">VS</div>
                    )}
                    <div className="match-team">
                        <img
                            src={team2?.image || placeholderImage}
                            alt={match.team_2_name}
                            className="team-logo-large"
                            onError={(e) => { e.target.src = placeholderImage }}
                        />
                        <div className="team-name-large">{match.team_2_name}</div>
                    </div>
                </div>
                <div className="match-info">
                    <div className="admin-info-item">
                        <span className="info-label">Date</span>
                        <span className="info-value">{formattedDate}</span>
                    </div>
                    <div className="admin-info-item">
                        <span className="info-label">Time</span>
                        <span className="info-value">{formattedTime}</span>
                    </div>
                    <div className="admin-info-item">
                        <span className="info-label">Format</span>
                        <span className="info-value">Best of {match.match_format.count}</span>
                    </div>
                    <div className="admin-info-item">
                        <span className="info-label">League</span>
                        <div className="league-badge info-value">
                            {/*<img
                                src={match.league_details?.image || placeholderImage}
                                alt={match.league}
                                onError={(e) => { e.target.src = placeholderImage }}
                            />*/}
                            <span>{match.league}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="admin-match-tabs">
                <div
                    className={`tab ${activeTab === 'games' ? 'active' : ''}`}
                    onClick={() => setActiveTab('games')}
                >
                    Games
                </div>
                <div
                    className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stats')}
                >
                    Player Stats
                </div>
                <div
                    className={`tab ${activeTab === 'streams' ? 'active' : ''}`}
                    onClick={() => setActiveTab('streams')}
                >
                    Streams
                </div>
                <div
                    className={`tab ${activeTab === 'roster' ? 'active' : ''}`}
                    onClick={() => setActiveTab('roster')}
                >
                    Rosters
                </div>
            </div>

            {/* Games Tab Content */}
            <div id="games-tab" className={`tab-content ${activeTab === 'games' ? 'active' : ''}`}>
                <MatchGamesList match={match} />
            </div>

            {/* Player Stats Tab Content */}
            <div id="stats-tab" className={`tab-content ${activeTab === 'stats' ? 'active' : ''}`}>
                {match.match_status !== 'completed' ? (
                    <div className="no-stats-message">
                        Player statistics will be available once the match is completed.
                    </div>
                ) : (
                    <div className="player-stats-container">
                        <div className="game-selector">
                            <span>Select Game:</span>
                            <div className="game-buttons">
                                {Array.from({ length: gamesCount }, (_, i) => i + 1).map(gameNum => (
                                    <button
                                        key={gameNum}
                                        className={`game-button ${activeGame === gameNum ? 'active' : ''}`}
                                        onClick={() => setActiveGame(gameNum)}
                                        disabled={!team1?.players?.some(p => p.results?.some(r => r.game_number === gameNum))}
                                    >
                                        {gameNum}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="teams-stats">
                            <div className="team-stats">
                                <h3>{match.team_1_name}</h3>
                                {renderPlayerStats(team1)}
                            </div>

                            <div className="team-stats">
                                <h3>{match.team_2_name}</h3>
                                {renderPlayerStats(team2)}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Streams Tab Content */}
            <div id="streams-tab" className={`tab-content ${activeTab === 'streams' ? 'active' : ''}`}>
                <StreamLinks links={match.stream_links} />
            </div>

            {/* Roster Tab Content */}
            <div id="roster-tab" className={`tab-content ${activeTab === 'roster' ? 'active' : ''}`}>
                <TeamRoster teams={match.teams} />
            </div>
        </div>
    );
};

export default MatchDetails;
